import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { formatDate, formatPeriod } from '../../lib/format/dates.ts';
import { isUpcoming } from '../../lib/scheduling/upcoming.ts';
import { ApiFailure, api, type ScheduleSummary } from '../api.ts';
import { Notice } from '../components/Notice.tsx';
import { ScrollRegion } from '../components/ScrollRegion.tsx';
import { copy } from '../copy/en-CA.ts';

const c = copy.history;

/**
 * "Upcoming" for a saved schedule that starts after today, else "Current"
 * for the one the home page shows. Between two schedules the home page
 * shows the next one; that row still reads "Upcoming", since it has not
 * started, rather than both words at once.
 */
function ScheduleBadge({ upcoming, current }: { upcoming: boolean; current: boolean }) {
  if (upcoming) return <span className="badge upcoming">{c.upcoming}</span>;
  if (current) return <span className="badge physician">{c.current}</span>;
  return null;
}

export function HistoryPage() {
  const [rows, setRows] = useState<ScheduleSummary[] | null>(null);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [today, setToday] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .schedules()
      .then((r) => {
        setRows(r.schedules.filter((s) => s.status === 'saved'));
        setCurrentId(r.currentId);
        setToday(r.today);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiFailure ? err.message : copy.errors.unexpected);
      });
  }, []);

  return (
    <>
      <div className="page-title">
        <h1>{c.title}</h1>
        <p>{c.intro}</p>
      </div>
      {error ? <Notice kind="error">{error}</Notice> : null}
      {rows?.length === 0 ? (
        <section className="card">
          <p className="empty">{c.empty}</p>
        </section>
      ) : null}
      {rows && rows.length > 0 ? (
        <ScrollRegion label={c.title}>
          <table>
            <thead>
              <tr>
                <th>{c.columns.name}</th>
                <th>{c.columns.period}</th>
                <th>{c.columns.weeks}</th>
                <th>{c.columns.saved}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id}>
                  <td>
                    <Link to={`/schedules/${s.id}`}>
                      <strong>{s.name}</strong>
                    </Link>{' '}
                    <ScheduleBadge upcoming={today !== null && isUpcoming(s, today)} current={s.id === currentId} />
                  </td>
                  <td>{formatPeriod(s.startDate, s.endDate)}</td>
                  <td>{s.weekCount}</td>
                  <td className="muted">{s.savedOn ? formatDate(s.savedOn) : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollRegion>
      ) : null}
    </>
  );
}
